import { Routes } from '@angular/router';
import { inject } from '@angular/core';

// rxjs
import { firstValueFrom } from 'rxjs';

// Guards
import { authGuard } from './auth/guards/auth.guard';
import { canDeactivateGuard } from './shared/guards/can-deactivate.guard';
import { ngxPermissionsGuard } from 'ngx-permissions';

// Services
import { UserService } from './users/services/user.service';
import { LicenseService } from './license/sevices/license.service';
import { AuthService } from './auth/services/auth.service';

// Components
import { LoginComponent } from './auth/components/login/login.component';
import { UserListComponent } from './users/components/user-list/user-list.component';
import { UserEditComponent } from './users/components/user-edit/user-edit.component';
import { GroupListComponent } from './groups/components/group-list/group-list.component';
import { GroupCreateComponent } from './groups/components/group-create/group-create.component';
import { GroupDetailComponent } from './groups/components/group-detail/group-detail.component';
import { ChangePasswordComponent } from './auth/components/change-password/change-password.component';
import { ContextListComponent } from './contexts/components/context-list/context-list.component';
import { ContextCreateComponent } from './contexts/components/context-create/context-create.component';
import { AccessDeniedComponent } from './shared/components/access-denied/access-denied.component';
import { ContextDetailComponent } from './contexts/components/context-detail/context-detail.component';
import { ContextManagementComponent } from './contexts/components/context-management/context-management.component';
import { LicenseDetailComponent } from './license/components/license-detail/license-detail.component';
import { ForgotPasswordComponent } from './auth/components/forgot-password/forgot-password.component';
import { UtilizationDashboardComponent } from './utilization/components/utilization-dashboard/utilization-dashboard.component';
import { FaqComponent } from './faq/components/faq/faq.component';


export const routes: Routes = [
    { path: '', redirectTo: 'users', pathMatch: 'full' },
    { path: 'access-denied', component: AccessDeniedComponent },
    {
        path: 'auth/login',
        component: LoginComponent,
        title: 'Login',
        resolve: {
            // Clear any existing session before loading the login component
            beLogout: () => {
                inject(AuthService).logout()?.subscribe({
                    next: () => {
                        console.debug('Logged out before login.');
                    }, error: (err) => {
                        console.error('Error during logout before login:', err);
                    }
                });
            }
        }
    },
    { path: 'auth/change-password', component: ChangePasswordComponent, title: 'Gestione Password' },
    { path: 'auth/forgot-password', component: ForgotPasswordComponent, title: 'Password Dimenticata' },
    // Users
    {
        path: 'users',
        canActivate: [authGuard, ngxPermissionsGuard],
        data: {
            permissions: {
                only: ['ROLE_MAD'],
                redirectTo: '/groups' // TODO: Change when enable dashboard
            }
        },
        children: [
            { path: '', component: UserListComponent, title: 'Gestione Utenti' },
            { path: 'new', component: UserEditComponent, title: 'Nuovo Utente' },
            {
                path: ':id',
                component: UserEditComponent, title: 'Modifica Utente',
                resolve: {
                    userDetail: ({ params }: { params: { id: string } }) => firstValueFrom(inject(UserService).userDetail(params['id']))
                }
            }
        ]
    },
    // Groups
    {
        path: 'groups',
        canActivate: [authGuard],
        children: [
            {
                path: '',
                component: GroupListComponent,
                title: 'Gestione Gruppi',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD', 'ROLE_ADM'],
                        redirectTo: '/access-denied'
                    }
                }
            },
            {
                path: 'new',
                component: GroupCreateComponent,
                title: 'Nuovo Gruppo',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD'],
                        redirectTo: '/access-denied'
                    }
                }
            },
            {
                path: ':id',
                component: GroupDetailComponent,
                title: 'Modifica Gruppo',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD'],
                        redirectTo: '/access-denied'
                    }
                }
            }
        ]
    },
    // Contexts
    {
        path: 'contexts',
        canActivate: [authGuard],
        children: [
            {
                path: '',
                component: ContextListComponent,
                title: 'Gestione Contesti',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD', 'ROLE_ADM'],
                        redirectTo: '/access-denied'
                    }
                }
            },
            {
                path: 'new',
                component: ContextCreateComponent,
                title: 'Nuovo Contesto',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD'],
                        redirectTo: '/access-denied'
                    }
                }
            },
            {
                path: ':id',
                component: ContextDetailComponent,
                title: 'Modifica Contesto',
                canActivate: [ngxPermissionsGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD'],
                        redirectTo: '/access-denied'
                    }
                }
            },
            {
                path: ':id/management',
                component: ContextManagementComponent,
                title: $localize`Gestione Contesto`,
                canActivate: [ngxPermissionsGuard],
                canDeactivate: [canDeactivateGuard],
                data: {
                    permissions: {
                        only: ['ROLE_MAD', 'ROLE_ADM'],
                        redirectTo: '/access-denied'
                    }
                }
            }
        ]
    },
    // Utilization
    {
        path: 'utilization',
        canActivate: [authGuard],
        children: [
            {
                path: '',
                component: UtilizationDashboardComponent, title: 'Utilizzo',
            }
        ]
    },
    // License
    {
        path: 'license',
        canActivate: [authGuard, ngxPermissionsGuard],
        data: {
            permissions: {
                only: ['ROLE_MAD'],
                redirectTo: '/groups'
            }
        },
        children: [
            {
                path: '',
                component: LicenseDetailComponent, title: 'Dettagli Licenza',
                resolve: {
                    licenseDetails: () => firstValueFrom(inject(LicenseService).getLicenseDetails())
                }
            }
        ]
    },
    // Faq
    {
        path: 'faq',
        canActivate: [authGuard],
        children: [
            {
                path: '',
                component: FaqComponent, title: 'Faq'
            }
        ]
    },
];